import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Box, Button, Typography, Container } from '@mui/material';
import LoadingPage from './LoadingPage';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function Login() {
  const { login, currentUser, error, loading } = useAuth();
  const navigate = useNavigate();
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const hasLoggedAccess = useRef(false);

  useEffect(() => {
    if (loading) return;

    if (currentUser) {
      navigate('/', { replace: true });
      return;
    }

    // Only log access once per component mount
    if (!hasLoggedAccess.current) {
      hasLoggedAccess.current = true;
      
      fetch(`${API_URL}/unauthorized-log`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          level: 'info',
          message: 'Login page accessed',
          timestamp: new Date().toISOString()
        })
      }).catch(error => console.error('Failed to log access:', error));
    }
  }, [currentUser, navigate, loading]);

  const handleLogin = async () => {
    try {
      setIsLoggingIn(true);
      await login();
    } catch (error) {
      console.error('Login error:', error);
      setIsLoggingIn(false);
    }
  };

  // Show loading page during auth check or login process
  if (loading || isLoggingIn) {
    return <LoadingPage />;
  }

  // Don't show login page if already logged in
  if (currentUser) {
    return <LoadingPage />;
  }

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 3
        }}
      >
        <Typography component="h1" variant="h3" gutterBottom>
          OSCAR
        </Typography>
        <Typography component="h2" variant="h5" color="textSecondary" gutterBottom>
          OpenSearch SSA Knowledge Base
        </Typography>

        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}

        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleLogin}
          sx={{ mt: 2 }}
        >
          Sign in / Sign up with AWS
        </Button>
      </Box>
    </Container>
  );
}

export default Login;
